import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ProjectShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
  { id: 'all', label: 'All Projects', icon: 'LayoutGrid' },
  { id: 'restaurant', label: 'Restaurants', icon: 'UtensilsCrossed' },
  { id: 'retail', label: 'Retail', icon: 'ShoppingBag' },
  { id: 'office', label: 'Office', icon: 'Building' },
  { id: 'industrial', label: 'Industrial', icon: 'Factory' }];


  const projects = [
  {
    id: 1,
    category: 'restaurant',
    title: "McDonald\'s Kitchen Overhaul - Sandton",
    client: "McDonald\'s South Africa",
    image: "https://images.unsplash.com/photo-1701682187994-4167ab918a04",
    imageAlt: "Modern commercial kitchen with stainless steel equipment, industrial grade ventilation system, and professional cooking stations in busy restaurant environment",
    duration: "6 weeks",
    scope: "Complete plumbing and electrical infrastructure replacement",
    challenges: "Zero downtime requirement during peak hours",
    solution: "Phased night-shift implementation with backup systems",
    results: ["30% efficiency improvement", "Full compliance certification"],
    services: ["Commercial Plumbing", "Electrical Systems", "Grease Trap Installation"]
  },
  {
    id: 2,
    category: 'restaurant',
    title: "KFC Multi-Location Electrical Upgrade",
    client: "KFC South Africa",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1789bf5e3-1767642859655.png",
    imageAlt: "Professional electrician in safety gear installing modern electrical panel with circuit breakers in commercial kitchen setting with bright fluorescent lighting",
    duration: "12 weeks",
    scope: "Electrical system modernization across 8 locations",
    challenges: "Coordinating multiple sites simultaneously",
    solution: "Dedicated project management with synchronized scheduling",
    results: ["8 locations upgraded", "40% energy cost reduction", "Enhanced safety compliance"],
    services: ["Electrical Diagnostics", "Panel Upgrades", "Emergency Systems"]
  },
  {
    id: 3,
    category: 'retail',
    title: "Shopping Center Plumbing Infrastructure",
    client: "Gateway Mall",
    image: "https://images.unsplash.com/photo-1491702779419-d17a8baa1cb5",
    imageAlt: "Large modern shopping mall interior with multiple levels, glass railings, natural lighting through skylights, and busy shoppers walking through wide corridors",
    duration: "10 weeks",
    scope: "Complete restroom and water system renovation",
    challenges: "Maintaining mall operations during construction",
    solution: "Sectional approach with temporary facilities",
    results: ["Zero customer complaints", "Water efficiency improved 45%", "ADA compliance achieved"],
    services: ["Commercial Plumbing", "Water Management", "Fixture Installation"]
  },
  {
    id: 4,
    category: 'office',
    title: "Corporate Office Electrical Retrofit",
    client: "Tech Park Johannesburg",
    image: "https://images.unsplash.com/photo-1683022928175-4a3f6dd3692d",
    imageAlt: "Modern corporate office building exterior with glass facade, steel framework, and contemporary architectural design against blue sky with white clouds",
    duration: "8 weeks",
    scope: "LED lighting conversion and power distribution upgrade",
    challenges: "Working around business hours",
    solution: "Weekend and after-hours installation schedule",
    results: ["60% lighting cost reduction", "Improved workspace quality", "Smart controls integration"],
    services: ["Electrical Installation", "Lighting Design", "Energy Management"]
  },
  {
    id: 5,
    category: 'industrial',
    title: "Manufacturing Facility Plumbing System",
    client: "Industrial Park Pretoria",
    image: "https://images.unsplash.com/photo-1670483111819-ed62eff77173",
    imageAlt: "Industrial manufacturing facility interior with large machinery, metal piping systems, overhead cranes, and workers in safety equipment on factory floor",
    duration: "14 weeks",
    scope: "Industrial-grade plumbing for production facility",
    challenges: "High-pressure system requirements",
    solution: "Custom-engineered solutions with redundancy",
    results: ["Zero system failures", "Scalable infrastructure", "Maintenance cost reduced 35%"],
    services: ["Industrial Plumbing", "Pressure Systems", "Preventive Maintenance"]
  },
  {
    id: 6,
    category: 'retail',
    title: "Retail Chain Electrical Standardization",
    client: "National Retail Group",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_186c85d85-1766589993527.png",
    imageAlt: "Bright retail store interior with modern LED track lighting, organized product shelves, clean white walls, and professional merchandising displays",
    duration: "16 weeks",
    scope: "Electrical system standardization across 15 stores",
    challenges: "Diverse building ages and configurations",
    solution: "Modular design approach with standardized components",
    results: ["Unified maintenance protocols", "Reduced emergency calls 50%", "Consistent customer experience"],
    services: ["Electrical Audits", "System Design", "Implementation"]
  }];


  const filteredProjects = activeCategory === 'all' ?
  projects :
  projects?.filter((project) => project?.category === activeCategory);

  return (
    <section id="portfolio" className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Briefcase" size={20} color="var(--color-primary)" />
            <span className="text-primary font-semibold text-sm font-accent">Project Portfolio</span>
          </div>
          <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Commercial Success Stories
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto font-body">
            Detailed case studies showcasing our problem-solving capabilities and project management excellence across diverse commercial sectors.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories?.map((category) =>
          <button
            key={category?.id}
            onClick={() => setActiveCategory(category?.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
            activeCategory === category?.id ?
            'bg-primary text-primary-foreground shadow-md' :
            'bg-card text-foreground hover:bg-muted'}`
            }>

              <Icon name={category?.icon} size={20} />
              <span className="font-body">{category?.label}</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {filteredProjects?.map((project) =>
          <div
            key={project?.id}
            className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden group">

              <div className="relative h-64 md:h-72 overflow-hidden">
                <Image
                src={project?.image}
                alt={project?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent"></div>
                <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-semibold font-accent">
                  {project?.duration}
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <h3 className="font-headline font-bold text-xl md:text-2xl text-foreground mb-2">
                    {project?.title}
                  </h3>
                  <p className="text-primary font-semibold font-body">{project?.client}</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1 font-headline">Project Scope:</p>
                    <p className="text-muted-foreground text-sm font-body">{project?.scope}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1 font-headline">Challenge:</p>
                    <p className="text-muted-foreground text-sm font-body">{project?.challenges}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1 font-headline">Solution:</p>
                    <p className="text-muted-foreground text-sm font-body">{project?.solution}</p>
                  </div>
                </div>

                <div className="mb-6 pb-6 border-b border-border">
                  <p className="text-sm font-semibold text-foreground mb-3 font-headline">Key Results:</p>
                  <div className="space-y-2">
                    {project?.results?.map((result, index) =>
                  <div key={index} className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-foreground font-body">{result}</span>
                      </div>
                  )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {project?.services?.map((service, index) =>
                <span
                  key={index}
                  className="px-3 py-1 bg-muted text-foreground text-xs rounded-full font-body">

                      {service}
                    </span>
                )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-6 font-body">
            Ready to discuss your commercial project requirements?
          </p>
          <a
            href="tel:0827935785"
            className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 rounded-lg font-semibold text-lg hover:bg-accent/90 transition-all emergency-cta">

            <Icon name="Phone" size={24} />
            <span className="font-accent">082 793 5785</span>
          </a>
        </div>
      </div>
    </section>);

};

export default ProjectShowcase;